import { Controller } from '@hotwired/stimulus';
import Sortable from 'sortablejs';

/**
 * RemarkStatuses::TableComponent companion controller — /parametres, onglet
 * « Comptes rendus », tableau des statuts de remarque.
 *
 * Quatre responsabilités, toutes LOCALES au formulaire (rien n'est envoyé ici : le
 * parent `form_with` PATCH les `remark_statuses_attributes` au clic sur Enregistrer) :
 *
 *   1. add        — clone le `<template>` (`__NEW_INDEX__` → `Date.now()`) et l'insère
 *                   TOUJOURS AVANT la ligne terminale, jamais après : l'ordre que
 *                   l'agence voit est l'ordre du sélecteur de statut, et « Soldé »
 *                   ferme la liste.
 *   2. remove     — arrache (rip out) une ligne NEUVE. Une ligne persistée ne se
 *                   supprime PAS d'ici : sa corbeille part sur la route DELETE via
 *                   `confirmation-modal-trigger`, parce que supprimer un statut porté
 *                   est une décision informée (décision 5), pas un `_destroy` muet.
 *   3. reorder    — instance SortableJS PRIVÉE, pas le `sortable_controller.js` du
 *                   projet : celui-là POSTe vers un `urlValue` à chaque glissé et ne
 *                   touche aucun champ `[position]`. Ici le réordonnancement doit
 *                   rester dans le formulaire jusqu'à Enregistrer, et réécrire les
 *                   `[position]` cachés. Mêmes raisons que
 *                   `regularization_template_builder_controller.js`, dont ce fichier
 *                   reprend le patron.
 *   4. aperçu + aides — repeint le badge d'aperçu de la ligne (libellé + couleur) et
 *                   rend les TROIS aides NON BLOQUANTES (couleur dupliquée,
 *                   libellé dupliqué, ressemblance avec « en retard »).
 *
 * INVARIANT DE BORNES — les deux lignes verrouillées encadrent la liste. Elles n'ont
 * pas de poignée, `filter: '[data-locked]'` les empêche d'être saisies, et `onMove`
 * refuse en plus tout dépôt qui placerait une autre ligne AVANT l'initiale ou APRÈS la
 * terminale : le filtre seul ne protège que la ligne tirée, jamais la destination.
 *
 * CE QUE L'ERB REND, ET POURQUOI LES REPLIS RESTENT — `RemarkStatuses::_row.html.erb`
 * pose `data-locked` ET `data-role="initial|terminal"` sur le `<tr>` d'une ligne
 * verrouillée, et `data-locked` une seconde fois sur le `<span>` du cadenas (c'est lui
 * que vise le `filter` de SortableJS). `_isLocked`, `_initialRow` et `_terminalRow`
 * acceptent néanmoins le marqueur par CONTENANCE et un repli POSITIONNEL : ils servent
 * un `<tr>` qui perdrait l'un des deux attributs, et leur silence serait total — un
 * plafond de verrous inerte, une ligne neuve insérée APRÈS « Soldé », sans rien lever.
 *
 * POURQUOI `_rows()` INTERROGE LE DOM au lieu de lire `this.rowTargets` :
 * l'enregistrement d'une cible Stimulus passe par un MutationObserver, donc ASYNCHRONE.
 * Juste après `insertAdjacentHTML`, `rowTargets` ignore encore la ligne neuve — le
 * plafond de 8 et la réécriture des positions seraient calculés sur l'avant-dernier
 * état. Le précédent `regularization_template_builder_controller.js` porte ce défaut.
 *
 * JAMAIS d'`innerHTML` avec une donnée d'agence (#591) : le libellé passe par
 * `textContent`, la couleur par `className` recopié depuis le `data-badge-class` servi
 * par le serveur (classes littérales, seules connues du JIT Tailwind).
 *
 * Targets:
 * - rowsContainer : le `<tbody>` glissable (hôte de l'instance SortableJS)
 * - row           : une ligne de statut (déclarée pour le contrat de l'ERB ; lue par
 *                   `querySelector` scopé, voir ci-dessus)
 * - template      : le `<template>` d'une ligne neuve, portant `__NEW_INDEX__`
 * - positionField : le `[position]` caché d'une ligne (réécrit au glissé et à l'ajout)
 * - addButton     : « Ajouter un statut », désactivé à 8 lignes vivantes
 * - preview       : le conteneur du badge d'aperçu d'une ligne
 * - hints         : la zone `aria-live="polite"` des trois aides non bloquantes
 *
 * Action map:
 *   click->remark-status-builder#add            (bouton « Ajouter un statut »)
 *   click->remark-status-builder#remove         (corbeille d'une ligne NEUVE)
 *   input->remark-status-builder#syncPreview    (champ Libellé)
 *   change->remark-status-builder#syncPreview   (radio du nuancier)
 *
 * ⚠️ Le bouton « Ajouter un statut » est un slot `toolbar` rendu AU-DESSUS du `<form>`,
 * et le `<template>` de clonage est rendu HORS de lui : l'élément qui porte
 * `data-controller` est la racine du composant, qui englobe les trois. Une cible ou une
 * action hors de la portée du contrôleur ne se câble pas, en silence — chaque cible est
 * donc gardée par son `has…Target` pour que ce cas dégrade au lieu de lever.
 *
 * @example
 * <div data-controller="remark-status-builder">
 *   <div>6 statuts, du plus calme au plus urgent.
 *     <button type="button"
 *             data-remark-status-builder-target="addButton"
 *             data-action="remark-status-builder#add">Ajouter un statut</button>
 *   </div>
 *   <form …>
 *     <table><tbody data-remark-status-builder-target="rowsContainer">
 *       <tr data-remark-status-builder-target="row">
 *         <td><span data-locked title="Posé sur toute remarque nouvelle. …">🔒</span></td>
 *         …
 *       </tr>
 *       <tr data-remark-status-builder-target="row">
 *         <td><button class="sortable-handle" aria-label="Déplacer « Rappel » …"></button></td>
 *         <td><input name="…[label]" data-action="input->remark-status-builder#syncPreview"></td>
 *         <td><input type="radio" name="…[color_token]" value="red:solid" class="sr-only"
 *                    data-badge-class="inline-flex … bg-danger-600 text-white"
 *                    data-action="change->remark-status-builder#syncPreview"></td>
 *         <td><span data-remark-status-builder-target="preview"><span>Rappel</span></span></td>
 *         <td><a data-action="click->remark-status-builder#remove">Retirer</a></td>
 *       </tr>
 *       <tr data-remark-status-builder-target="row">… <span data-locked>🔒</span> …</tr>
 *     </tbody></table>
 *   </form>
 *   <div aria-live="polite" data-remark-status-builder-target="hints"></div>
 *   <template data-remark-status-builder-target="template">…__NEW_INDEX__…</template>
 * </div>
 */

/** Décision 20 : la liste est bornée à 8 statuts, garde serveur 422 en miroir. */
const MAX_STATUSES = 8;

/**
 * Littéral, jamais interpolé depuis `MAX_STATUSES` : c'est MOT POUR MOT la phrase
 * servie par l'i18n (`remark_statuses_max`), et on veut pouvoir la grepper des deux
 * côtés de la barrière. Espace simple avant le deux-points, comme le serveur.
 */
const MAX_REACHED_TITLE = 'Maximum atteint : 8 statuts.';

/** Ce que l'aperçu affiche tant que la ligne neuve n'a pas de libellé. */
const PREVIEW_PLACEHOLDER = 'Statut';

/**
 * Aide non bloquante : deux statuts partagent une couleur.
 *
 * Les insécables de l'intérieur des guillemets français sont écrites en échappement
 * pour qu'on les VOIE en revue : aplaties en espaces ordinaires par un copier-coller,
 * elles ne se remarqueraient pas autrement.
 *
 * La ligne NOMMÉE est la PREMIÈRE de l'ordre du tableau à porter la couleur, pas
 * forcément celle que l'agence vient de modifier.
 */
const DUPLICATE_COLOR_HINT = 'Couleur déjà utilisée par «\u00a0__LABEL__\u00a0».';

/**
 * Aide non bloquante : deux lignes portent le même libellé.
 *
 * ⚠️ CELLE-CI EXISTE POUR RENDRE UN 422 PRESQUE INATTEIGNABLE, pas pour décorer.
 * `update_remark_statuses` refuse l'ensemble soumis (`remark_status_label_taken`)
 * et son gabarit d'erreur REPEINT la section depuis l'état PERSISTÉ : un admin qui
 * renomme deux statuts, en ajoute un troisième et se fait refuser pour UN libellé perd
 * toute sa saisie, alors que le message ne lui demande de corriger qu'un champ.
 *
 * La phrase reprend MOT POUR MOT celle du serveur, pour qu'on puisse la grepper des
 * deux côtés de la barrière — même discipline que MAX_REACHED_TITLE.
 *
 * Comme pour la couleur, la ligne NOMMÉE est la PREMIÈRE de l'ordre du tableau à
 * porter ce libellé, pas forcément celle que l'agence vient de saisir.
 */
const DUPLICATE_LABEL_HINT = 'Ce libellé est déjà utilisé : «\u00a0__LABEL__\u00a0».';

/** Le slot partagé par les deux aides de doublon. */
const HINT_LABEL_PLACEHOLDER = '__LABEL__';

/**
 * Aide non bloquante : « En retard » n'est pas un statut mais un signal dérivé de
 * l'échéance, affiché À CÔTÉ du statut (décision 7). Un statut qui lui ressemble ferait
 * lire deux fois la même information.
 */
const OVERDUE_LOOKALIKE_HINT =
  "Ce libellé peut prêter à confusion avec l'échéance dépassée, déjà affichée séparément.";

/** Testé sur le libellé NORMALISÉ (casse, accents, espaces) : « En Retard », « retards »… */
const OVERDUE_LOOKALIKE_PATTERN = /retard/;

/** Les deux rôles verrouillés : renommables, jamais supprimables ni déplaçables. */
const LOCKED_ROLES = ['initial', 'terminal'];

/**
 * Le marqueur de verrou tel que l'ERB le rend : sur le `<span>` du cadenas, DANS la
 * ligne. C'est aussi le `filter` de SortableJS, d'où la constante partagée.
 */
const LOCKED_MARKER_SELECTOR = '[data-locked]';

const ROW_SELECTOR = '[data-remark-status-builder-target="row"]';
const POSITION_SELECTOR = '[data-remark-status-builder-target="positionField"]';
const PREVIEW_SELECTOR = '[data-remark-status-builder-target="preview"]';
const LABEL_SELECTOR = 'input[name$="[label]"]';
const CHECKED_COLOR_SELECTOR = 'input[name$="[color_token]"]:checked';
const PERSISTED_ID_SELECTOR = 'input[name$="[id]"]';
const NEW_INDEX_TOKEN = /__NEW_INDEX__/g;

export default class extends Controller {
  static targets = [
    'rowsContainer',
    'row',
    'template',
    'positionField',
    'addButton',
    'preview',
    'hints',
  ];

  connect() {
    this._initSortable();
    this._refreshAddButton();
    this._refreshHints();
  }

  disconnect() {
    this.sortable?.destroy();
    this.sortable = null;
  }

  /**
   * Ajoute une ligne neuve AVANT la ligne terminale.
   *
   * @param {Event} [event]
   */
  add(event) {
    event?.preventDefault();
    if (!this.hasTemplateTarget || !this.hasRowsContainerTarget) return;
    // Ceinture : le bouton est déjà `disabled` à 8. Un double-clic rapide, ou une
    // action recâblée ailleurs, ne doit pas pouvoir franchir la borne côté client.
    if (this._rows().length >= MAX_STATUSES) return;

    const html = this.templateTarget.innerHTML.replace(NEW_INDEX_TOKEN, this._nextNewIndex());
    const terminal = this._terminalRow();

    if (terminal) {
      terminal.insertAdjacentHTML('beforebegin', html);
    } else {
      this.rowsContainerTarget.insertAdjacentHTML('beforeend', html);
    }

    this._rewritePositionFields();
    this._refreshAddButton();
    this._refreshHints();
    this._focusInsertedRow(terminal);
  }

  /**
   * Arrache une ligne NEUVE. Une ligne persistée sort par la route DELETE.
   *
   * @param {Event} event
   */
  remove(event) {
    event?.preventDefault();
    const row = this._rowOf(event);
    if (!row || this._isLocked(row) || this._isPersisted(row)) return;

    row.remove();
    this._rewritePositionFields();
    this._refreshAddButton();
    this._refreshHints();
  }

  /**
   * Repeint l'aperçu de la ligne d'où vient l'événement, puis rejoue les aides.
   *
   * @param {Event} event
   */
  syncPreview(event) {
    const row = this._rowOf(event);
    if (!row) return;

    this._repaintPreview(row);
    this._refreshHints();
  }

  _initSortable() {
    if (!this.hasRowsContainerTarget) return;

    this.sortable = Sortable.create(this.rowsContainerTarget, {
      handle: '.sortable-handle',
      // Les lignes verrouillées ne se saisissent pas. `preventOnFilter: false` laisse
      // le clic vivre : la poignée verrouillée est un `<span title=…>`, son infobulle
      // est l'explication, et l'avaler rendrait le refus muet.
      filter: LOCKED_MARKER_SELECTOR,
      preventOnFilter: false,
      animation: 150,
      onMove: (event) => this._allowsMove(event),
      onEnd: () => this._rewritePositionFields(),
    });
  }

  /**
   * Refuse tout dépôt hors des bornes : avant la ligne initiale, après la terminale.
   *
   * Les bornes sont comparées par IDENTITÉ aux lignes rendues (`_initialRow` /
   * `_terminalRow`) et non par lecture d'un `data-role` sur `related` : l'ERB ne pose
   * le rôle sur aucune ligne, voir `_isLocked`.
   *
   * La borne positionnelle reste vraie PAR INDUCTION pendant un glissé : SortableJS
   * consulte `onMove` AVANT chaque insertion, donc refuser suffit à garantir que la
   * première et la dernière ligne restent les deux verrouillées.
   *
   * @param {{ dragged?: Element, related?: Element, willInsertAfter?: boolean }} event
   * @returns {boolean}
   */
  _allowsMove(event) {
    const { dragged, related, willInsertAfter } = event;
    if (dragged && this._isLocked(dragged)) return false;
    if (!related) return true;
    if (related === this._initialRow() && !willInsertAfter) return false;
    if (related === this._terminalRow() && willInsertAfter) return false;
    return true;
  }

  _rewritePositionFields() {
    this._rows().forEach((row, index) => {
      const field = row.querySelector(POSITION_SELECTOR);
      if (field) field.value = String(index);
    });
  }

  _refreshAddButton() {
    if (!this.hasAddButtonTarget) return;

    const full = this._rows().length >= MAX_STATUSES;
    this.addButtonTarget.disabled = full;
    // En miroir de `disabled`, comme le rendu serveur (`aria: { disabled: }`) : c'est
    // l'attribut ARIA que les lecteurs d'écran relaient.
    this.addButtonTarget.setAttribute('aria-disabled', String(full));

    if (full) {
      this.addButtonTarget.setAttribute('title', MAX_REACHED_TITLE);
    } else {
      this.addButtonTarget.removeAttribute('title');
    }
  }

  /**
   * Rend les aides non bloquantes, une ligne par aide, aucune si tout va bien.
   *
   * Elles INFORMENT : rien n'est désactivé, rien n'empêche d'enregistrer. Un libellé
   * vide n'est jamais nommé comme propriétaire d'une couleur ni d'un libellé —
   * « déjà utilisée par «  » » ne dit rien.
   *
   * UN SEUL BALAYAGE pour les trois aides : le doublon de couleur, le doublon de
   * libellé et la ressemblance avec « en retard ». Une seconde mécanique de
   * rafraîchissement dériverait de celle-ci (le `Set` de dédoublonnage, l'ordre des
   * lignes, la garde `hasHintsTarget`) sans que rien ne le signale.
   */
  _refreshHints() {
    if (!this.hasHintsTarget) return;

    const messages = [];
    const ownerByToken = new Map();
    const ownerByLabelKey = new Map();

    this._rows().forEach((row) => {
      const label = this._labelOf(row);
      const token = row.querySelector(CHECKED_COLOR_SELECTOR)?.value ?? '';

      if (token !== '') {
        const owner = ownerByToken.get(token);
        if (owner === undefined) {
          ownerByToken.set(token, label);
        } else if (owner !== '') {
          messages.push(DUPLICATE_COLOR_HINT.replace(HINT_LABEL_PLACEHOLDER, owner));
        }
      }

      const labelKey = this._labelKey(label);
      if (labelKey !== '') {
        const owner = ownerByLabelKey.get(labelKey);
        if (owner === undefined) {
          ownerByLabelKey.set(labelKey, label);
        } else {
          messages.push(DUPLICATE_LABEL_HINT.replace(HINT_LABEL_PLACEHOLDER, owner));
        }
      }

      if (OVERDUE_LOOKALIKE_PATTERN.test(this._normalize(label))) {
        messages.push(OVERDUE_LOOKALIKE_HINT);
      }
    });

    this.hintsTarget.replaceChildren();

    // `textContent` sur un `<p>` créé ici : la mise en forme appartient au conteneur
    // servi par l'ERB, le contrôleur ne fournit que du texte.
    [...new Set(messages)].forEach((message) => {
      const line = document.createElement('p');
      line.textContent = message;
      this.hintsTarget.appendChild(line);
    });
  }

  _repaintPreview(row) {
    const badge = this._badgeOf(row);
    if (!badge) return;

    const label = this._labelOf(row);
    const shown = label === '' ? PREVIEW_PLACEHOLDER : label;
    // Le libellé vit dans l'ENFANT bloc tronqué du badge (`block max-w-28 truncate`,
    // MeetingReports::RemarkStatusBadgeComponent) : c'est lui qu'on réécrit. Un
    // `textContent` sur la racine détruirait cet enfant, et la troncature avec lui.
    // Repli sur la racine pour un badge rendu sans enfant.
    (badge.firstElementChild ?? badge).textContent = shown;
    // Le badge serveur porte `title` pour les libellés tronqués : le garder à jour
    // évite une infobulle qui contredit le texte.
    badge.setAttribute?.('title', shown);

    const badgeClass = row.querySelector(CHECKED_COLOR_SELECTOR)?.dataset?.badgeClass;
    if (badgeClass) badge.className = badgeClass;
  }

  /**
   * Donne le focus au champ Libellé de la ligne qui vient d'être insérée.
   *
   * Le bouton « Ajouter » est au-dessus du tableau : sans ce déplacement, un lecteur
   * d'écran n'apprend rien de l'apparition de la ligne.
   *
   * @param {Element|null} terminal
   */
  _focusInsertedRow(terminal) {
    const inserted = terminal
      ? terminal.previousElementSibling
      : this.rowsContainerTarget.lastElementChild;
    if (!inserted?.matches?.(ROW_SELECTOR)) return;

    inserted.querySelector(LABEL_SELECTOR)?.focus?.();
  }

  /**
   * L'index de nested attributes d'une ligne neuve : `Date.now()`, rendu MONOTONE.
   *
   * Deux ajouts dans la même milliseconde produiraient le même index, donc la même
   * clé `remark_statuses_attributes[…]` : Rails fusionnerait les deux lignes en une
   * seule et l'agence perdrait un statut sans le moindre message.
   *
   * @returns {string}
   */
  _nextNewIndex() {
    const now = Date.now();
    this.lastNewIndex =
      this.lastNewIndex === undefined ? now : Math.max(now, this.lastNewIndex + 1);
    return String(this.lastNewIndex);
  }

  _rows() {
    if (!this.hasRowsContainerTarget) return [];
    return [...this.rowsContainerTarget.querySelectorAll(ROW_SELECTOR)];
  }

  /**
   * La ligne qui FERME la liste — celle du rôle terminal (« Soldé » par défaut).
   *
   * Le `data-role` du `<tr>` est lu d'abord ; à défaut, la DERNIÈRE ligne si elle est
   * verrouillée. Le repli positionnel tient parce que le serveur rend toujours
   * l'initiale en tête et la terminale en queue (`RemarkStatuses::NormalizePositions`)
   * et que `_allowsMove` + `add` préservent cet encadrement.
   *
   * @returns {Element|null}
   */
  _terminalRow() {
    const rows = this._rows();
    const byRole = rows.find((row) => row.dataset?.role === 'terminal');
    if (byRole) return byRole;

    const last = rows.at(-1);
    return last && this._isLocked(last) ? last : null;
  }

  /**
   * La ligne qui OUVRE la liste — celle du rôle initial (« Ouvert » par défaut).
   *
   * Même repli positionnel que `_terminalRow`.
   *
   * @returns {Element|null}
   */
  _initialRow() {
    const rows = this._rows();
    const byRole = rows.find((row) => row.dataset?.role === 'initial');
    if (byRole) return byRole;

    const first = rows[0];
    return first && this._isLocked(first) ? first : null;
  }

  _rowOf(event) {
    const origin = event?.currentTarget ?? event?.target;
    return origin?.closest?.(ROW_SELECTOR) ?? null;
  }

  /**
   * Une ligne est verrouillée si elle PORTE le marqueur, déclare son rôle, ou CONTIENT
   * le marqueur.
   *
   * L'ERB pose les deux premières formes sur le `<tr>` ; la troisième couvre le
   * `data-locked` du `<span>` du cadenas, seul marqueur d'une ligne qui perdrait ses
   * attributs. Ne lire que `row.dataset.role` rendrait ce prédicat — donc le refus de
   * supprimer un verrou ET les deux bornes du glissé — INERTE, sans rien lever.
   *
   * @param {Element} row
   * @returns {boolean}
   */
  _isLocked(row) {
    if (row.hasAttribute?.('data-locked')) return true;
    if (LOCKED_ROLES.includes(row.dataset?.role)) return true;
    return Boolean(row.querySelector?.(LOCKED_MARKER_SELECTOR));
  }

  _isPersisted(row) {
    const field = row.querySelector(PERSISTED_ID_SELECTOR);
    return Boolean(field?.value);
  }

  _labelOf(row) {
    return row.querySelector(LABEL_SELECTOR)?.value?.trim() ?? '';
  }

  _badgeOf(row) {
    const preview = row.querySelector(PREVIEW_SELECTOR);
    if (!preview) return null;
    // La cible est le CONTENEUR de l'aperçu et le badge son premier enfant. Le repli
    // couvre une ERB qui poserait la cible directement sur le badge.
    return preview.firstElementChild ?? preview;
  }

  /**
   * Le pli de l'aide « en retard » : casse, accents ET espaces. Il vise une
   * RESSEMBLANCE, donc il plie large.
   *
   * @param {string} value
   * @returns {string}
   */
  _normalize(value) {
    return value
      .toLowerCase()
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  /**
   * Le pli de l'UNICITÉ, MIROIR EXACT du serveur (`RemarkStatuses::SaveBatch#normalize`
   * : `gsub(/\s+/, ' ').strip.downcase`) : casse et espaces, JAMAIS les accents.
   *
   * ⚠️ NE PAS RÉUTILISER `_normalize` ICI. L'index unique est sur `lower(label)`, donc
   * « Solde » et « Soldé » sont deux libellés que le serveur ACCEPTE : plier les
   * accents ferait crier l'aide sur une saisie que l'Enregistrer suivant passerait
   * sans broncher — une aide qui ment sur ce que le serveur va faire est pire que pas
   * d'aide, puisque c'est exactement la prédiction qu'on lui demande.
   *
   * @param {string} value
   * @returns {string}
   */
  _labelKey(value) {
    return value.replace(/\s+/g, ' ').trim().toLowerCase();
  }
}
